import { ConfirmModal } from "@components/ConfirmModal";
import { OverlaySpinner } from "@components/Loading/OverlaySpinner";
import { downloadAsFetchLink } from "@helpers/download";
import { FirebaseContext } from "@helpers/firebase";
import { useClaims } from "@helpers/useClaims";
import { useToast } from "@helpers/useToast";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import ReactTable from "react-table";
import "react-table/react-table.css";

const filterCaseInsensitive = (filter, row) => {
  const id = filter.pivotId || filter.id;
  return row[id] !== undefined ? String(row[id].toLowerCase()).includes(filter.value.toLowerCase()) : true;
};

type EventWithStats = BasicEvent & {
  numberOfTeams?: number;
  numberOfSolutions?: number;
  numberOfExtras?: number;
  statsLoading?: boolean;
  statsLoaded?: boolean;
};

// "demo" / "onninen" are baseline claims — they must never be unassumed,
// or the admin account would lose access to the dashboard itself.
const PROTECTED_ASSUMED_EVENTS = ["demo", "onninen"];

export const EventTable = ({ data }: { data: BasicEvent[] }) => {
  const { functions, auth } = useContext(FirebaseContext);
  const [generateInvoice, generatingInvoice, invoiceError] = useHttpsCallable<
    API.GenerateInvoiceRequest,
    API.GenerateInvoiceResponse
  >(functions, "generateInvoice");
  const [getEventStats, , statsError] = useHttpsCallable<API.GetEventStatsRequest, API.GetEventStatsResponse>(
    functions,
    "getEventStats",
  );
  const [setPremium, settingPremium, setPremiumError] = useHttpsCallable<API.SetPremiumRequest, API.SetPremiumResponse>(
    functions,
    "setPremiumV2",
  );
  const [removePremium, removingPremium, removePremiumError] = useHttpsCallable<
    API.RemovePremiumRequest,
    API.RemovePremiumResponse
  >(functions, "removePremiumV2");
  const [setEventPromo, settingPromo, setPromoError] = useHttpsCallable<
    API.SetEventPromoRequest,
    API.SetEventPromoResponse
  >(functions, "setEventPromoV2");
  const [assumeEvent, assuming, assumeError] = useHttpsCallable<API.AssumeEventRequest, API.AssumeEventResponse>(
    functions,
    "assumeEventV2",
  );
  const [claims] = useClaims(auth.currentUser);

  const [eventsWithStats, setEventsWithStats] = useState<EventWithStats[]>([]);
  const [eventToProcess, setEventToProcess] = useState<{ id: string; action: "set" | "remove"; name: string } | null>(
    null,
  );
  const [promoToProcess, setPromoToProcess] = useState<{ id: string; action: "set" | "remove"; name: string } | null>(
    null,
  );
  const [assumeToProcess, setAssumeToProcess] = useState<{
    id: string;
    name: string;
    action: "assume" | "unassume";
  } | null>(null);
  const [assumedEvents, setAssumedEvents] = useState<string[]>([]);
  const toast = useToast();

  useEffect(() => {
    if (invoiceError || statsError || setPremiumError || removePremiumError || setPromoError || assumeError)
      toast("something went wrong", "🤷‍♂️", "error");
  }, [invoiceError, statsError, setPremiumError, removePremiumError, setPromoError, assumeError]);

  // Initialize events with stats when data changes
  useEffect(() => {
    setEventsWithStats(data.map((event) => ({ ...event, statsLoading: false, statsLoaded: false })));
  }, [data]);

  // Seed the assumed-events list from the current user's claims.
  useEffect(() => {
    if (claims?.events) setAssumedEvents(claims.events);
  }, [claims]);

  const onDownloadInvoice = async (eventId: string) => {
    const response = await generateInvoice({ eventId });
    downloadAsFetchLink(response.data.url, `tuokis-${eventId}-invoice.pdf`);
  };

  const loadEventStats = async (eventId: string) => {
    setEventsWithStats((prev) =>
      prev.map((event) => (event.id === eventId ? { ...event, statsLoading: true } : event)),
    );

    try {
      const result = await getEventStats({ eventId });
      if (result?.data) {
        setEventsWithStats((prev) =>
          prev.map((event) =>
            event.id === eventId
              ? {
                  ...event,
                  numberOfTeams: result.data.numberOfTeams,
                  numberOfSolutions: result.data.numberOfSolutions,
                  numberOfExtras: result.data.numberOfExtras,
                  statsLoading: false,
                  statsLoaded: true,
                }
              : event,
          ),
        );
      }
    } catch (error) {
      console.error("Error loading event stats:", error);
      setEventsWithStats((prev) =>
        prev.map((event) => (event.id === eventId ? { ...event, statsLoading: false } : event)),
      );
      toast("Failed to load stats", "❌", "error");
    }
  };

  const handleSetPremium = (eventId: string) => {
    const event = eventsWithStats.find((e) => e.id === eventId);
    setEventToProcess({ id: eventId, action: "set", name: event?.name || eventId });
  };

  const handleRemovePremium = (eventId: string) => {
    const event = eventsWithStats.find((e) => e.id === eventId);
    setEventToProcess({ id: eventId, action: "remove", name: event?.name || eventId });
  };

  const confirmPremiumAction = async () => {
    if (!eventToProcess) return;

    try {
      if (eventToProcess.action === "set") {
        const response = await setPremium({ eventId: eventToProcess.id });
        if (response?.data?.success) {
          // Update local state optimistically
          setEventsWithStats((prev) =>
            prev.map((event) => (event.id === eventToProcess.id ? { ...event, isPremium: true } : event)),
          );
          toast("Event set to premium successfully!", "🎉", "success");
        }
      } else {
        const response = await removePremium({ eventId: eventToProcess.id });
        if (response?.data?.success) {
          // Update local state optimistically
          setEventsWithStats((prev) =>
            prev.map((event) => (event.id === eventToProcess.id ? { ...event, isPremium: false } : event)),
          );
          toast("Premium status removed successfully!", "🗑️", "success");
        }
      }
    } catch (err) {
      console.error("Error managing premium status:", err);
      toast("Failed to update premium status", "❌", "error");
    } finally {
      setEventToProcess(null);
    }
  };

  const handleSetPromo = (eventId: string) => {
    const event = eventsWithStats.find((e) => e.id === eventId);
    setPromoToProcess({ id: eventId, action: "set", name: event?.name || eventId });
  };

  const handleRemovePromo = (eventId: string) => {
    const event = eventsWithStats.find((e) => e.id === eventId);
    setPromoToProcess({ id: eventId, action: "remove", name: event?.name || eventId });
  };

  const confirmPromoAction = async () => {
    if (!promoToProcess) return;

    try {
      const isPromo = promoToProcess.action === "set";
      const response = await setEventPromo({ eventId: promoToProcess.id, isPromo });
      if (response?.data?.success) {
        // Update local state optimistically
        setEventsWithStats((prev) =>
          prev.map((event) => (event.id === promoToProcess.id ? { ...event, isPromo } : event)),
        );
        toast(
          isPromo ? "Event set as promo successfully!" : "Promo status removed successfully!",
          isPromo ? "🎉" : "🗑️",
          "success",
        );
      }
    } catch (err) {
      console.error("Error managing promo status:", err);
      toast("Failed to update promo status", "❌", "error");
    } finally {
      setPromoToProcess(null);
    }
  };

  const handleToggleAssume = (eventId: string) => {
    const event = eventsWithStats.find((e) => e.id === eventId);
    const action = assumedEvents.includes(eventId) ? "unassume" : "assume";
    setAssumeToProcess({ id: eventId, name: event?.name || eventId, action });
  };

  const confirmAssumeAction = async () => {
    if (!assumeToProcess) return;

    const assume = assumeToProcess.action === "assume";
    try {
      const response = await assumeEvent({ eventId: assumeToProcess.id, assume });
      if (response?.data?.success) {
        // Trust the backend's authoritative list (it may trim old events when assuming).
        setAssumedEvents(response.data.events);
        // Force-refresh the ID token so the updated event claims take effect immediately.
        await auth.currentUser?.getIdToken(true);
        toast(
          assume ? "Event assumed — added to your admin account" : "Event unassumed — removed from your admin account",
          assume ? "🎭" : "🗑️",
          "success",
        );
      }
    } catch (err) {
      console.error("Error updating assumed events:", err);
      toast(assume ? "Failed to assume event" : "Failed to unassume event", "❌", "error");
    } finally {
      setAssumeToProcess(null);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const isWithin30Days = (dateStr: string) => {
    if (!dateStr) return false;
    const today = new Date();
    const date = new Date(dateStr);
    // @ts-ignore
    const isNotInPast = date > today.setDate(today.getDate() - 1);
    // @ts-ignore
    const isNotLaterThan30DaysFromNow = date < today.setDate(today.getDate() + 30);

    return isNotInPast && isNotLaterThan30DaysFromNow;
  };

  const sortedData = useMemo(() => {
    return [...eventsWithStats].sort((a, b) => {
      const adate = a.date ? new Date(a.date as string) : new Date("1970-01-01");
      const bdate = b.date ? new Date(b.date as string) : new Date("1970-01-01");
      // @ts-ignore
      return bdate - adate;
    });
  }, [eventsWithStats]);

  const [showPremium, setShowPremium] = useState(true);
  const [showUpcoming, setShowUpcoming] = useState(false);

  const [tableData, setTableData] = useState([]);
  useEffect(() => {
    if (sortedData) {
      const tableData = sortedData
        .map((item) => {
          const {
            id,
            isPremium,
            isPromo,
            date,
            numberOfTeams,
            numberOfSolutions,
            numberOfExtras,
            statsLoading,
            statsLoaded,
          } = item;
          const isAssumed = assumedEvents.includes(id);
          const isAssumeProtected = PROTECTED_ASSUMED_EVENTS.includes(id);
          return {
            id,
            date: formatDate(date),
            numberOfTeams: statsLoaded ? (
              numberOfTeams
            ) : (
              <button className={`btn-sm btn my-0 p-1`} onClick={() => loadEventStats(id)} disabled={statsLoading}>
                {statsLoading ? "Loading..." : "Load Stats"}
              </button>
            ),
            numberOfSolutions: statsLoaded ? numberOfSolutions : "-",
            numberOfExtras: statsLoaded ? numberOfExtras : "-",
            isPremium: isPremium ? "YES" : "",
            actions: (
              <div className="actions-right">
                <button
                  className={`btn-sm btn my-0 p-1 ${generatingInvoice ? "loading" : ""}`}
                  onClick={() => onDownloadInvoice(id)}
                  disabled={!isPremium || generatingInvoice}
                >
                  Download
                </button>
              </div>
            ),
            premiumActions: (
              <div className="actions-right flex gap-1">
                {isPremium ? (
                  <button
                    className={`btn-error btn-sm btn my-0 p-1 ${removingPremium ? "loading" : ""}`}
                    onClick={() => handleRemovePremium(id)}
                    disabled={settingPremium || removingPremium}
                  >
                    Remove
                  </button>
                ) : (
                  <button
                    className={`btn-success btn-sm btn my-0 p-1 ${settingPremium ? "loading" : ""}`}
                    onClick={() => handleSetPremium(id)}
                    disabled={settingPremium || removingPremium}
                  >
                    Set Premium
                  </button>
                )}
              </div>
            ),
            promoActions: (
              <div className="actions-right flex gap-1">
                {isPromo ? (
                  <button
                    className={`btn-error btn-sm btn my-0 p-1 ${settingPromo ? "loading" : ""}`}
                    onClick={() => handleRemovePromo(id)}
                    disabled={settingPromo}
                  >
                    Remove
                  </button>
                ) : (
                  <button
                    className={`btn-success btn-sm btn my-0 p-1 ${settingPromo ? "loading" : ""}`}
                    onClick={() => handleSetPromo(id)}
                    disabled={settingPromo}
                  >
                    Set Promo
                  </button>
                )}
              </div>
            ),
            assumeActions: isAssumeProtected ? (
              <div className="actions-right flex gap-1">
                <button className="btn-sm btn my-0 p-1" disabled>
                  Assumed 🔒
                </button>
              </div>
            ) : (
              <div className="actions-right flex gap-1">
                <button
                  className={`${isAssumed ? "btn-error" : "btn-success"} btn-sm btn my-0 p-1 ${
                    assuming ? "loading" : ""
                  }`}
                  onClick={() => handleToggleAssume(id)}
                  disabled={assuming}
                >
                  {isAssumed ? "Unassume" : "Assume"}
                </button>
              </div>
            ),
          };
        })
        .filter((item) => {
          if (showPremium) return item.isPremium === "YES";
          return true;
        })
        .filter((item) => {
          if (showUpcoming) return isWithin30Days(item.date);
          return true;
        });
      setTableData(tableData);
    }
  }, [
    sortedData,
    showPremium,
    showUpcoming,
    generatingInvoice,
    settingPremium,
    removingPremium,
    settingPromo,
    assuming,
    assumedEvents,
  ]);

  return (
    <div className="flex flex-col justify-center sm:mx-12">
      <OverlaySpinner loading={generatingInvoice || settingPremium || removingPremium || settingPromo || assuming} />
      <div className="mb-6 flex max-w-sm flex-col">
        <div className="form-control mt-4">
          <label className="label cursor-pointer" htmlFor={"upcoming"}>
            <div className="label-text mr-3 text-sm">
              <span className="label-text">Upcoming (within 30 days)</span>
            </div>
            <input
              id={"upcoming"}
              type="checkbox"
              className={`toggle-primary toggle`}
              checked={showUpcoming}
              onClick={(e) => {
                const value = (e.target as HTMLInputElement).checked;
                setShowUpcoming(value);
              }}
            />
          </label>
        </div>
        <div className="form-control">
          <label className="label cursor-pointer" htmlFor={"upcoming"}>
            <div className="label-text mr-3 text-sm">
              <span className="label-text">Only premium</span>
            </div>
            <input
              id={"upcoming"}
              type="checkbox"
              className={`toggle-primary toggle`}
              checked={showPremium}
              onClick={(e) => {
                const value = (e.target as HTMLInputElement).checked;
                setShowPremium(value);
              }}
            />
          </label>
        </div>
      </div>
      <div className="mb-4 text-sm">
        <span className="font-semibold">Currently assumed events:</span>{" "}
        {assumedEvents.length > 0 ? assumedEvents.join(", ") : "none"}
      </div>
      <ReactTable
        data={tableData}
        filterable
        defaultFilterMethod={filterCaseInsensitive}
        columns={[
          {
            Header: "ID",
            accessor: "id",
            width: 150,
          },
          {
            Header: "Date",
            accessor: "date",
            minWidth: 120,
          },
          {
            Header: "Teams",
            accessor: "numberOfTeams",
            maxWidth: 100,
            sortable: false,
          },
          {
            Header: "Solutions",
            accessor: "numberOfSolutions",
            maxWidth: 80,
            sortable: false,
          },
          {
            Header: "Extras",
            accessor: "numberOfExtras",
            maxWidth: 80,
            sortable: false,
          },
          {
            Header: "Premium",
            accessor: "premiumActions",
            sortable: false,
            filterable: false,
            maxWidth: 120,
          },
          {
            Header: "Promo",
            accessor: "promoActions",
            sortable: false,
            filterable: false,
            maxWidth: 120,
          },
          {
            Header: "Assume",
            accessor: "assumeActions",
            sortable: false,
            filterable: false,
            maxWidth: 120,
          },
          {
            Header: "Invoice",
            accessor: "actions",
            sortable: false,
            filterable: false,
            maxWidth: 120,
          },
        ]}
        defaultPageSize={10}
        showPaginationBottom
        className="w-full text-center"
      />

      <ConfirmModal
        show={!!eventToProcess}
        onCancel={() => setEventToProcess(null)}
        onConfirm={confirmPremiumAction}
        messageCopy={
          eventToProcess?.action === "set"
            ? `Set "${eventToProcess.name}" (${eventToProcess.id}) as premium?`
            : `Remove premium status from "${eventToProcess?.name}" (${eventToProcess?.id})?`
        }
        actionCopy={eventToProcess?.action === "set" ? "Set Premium" : "Remove Premium"}
        type={eventToProcess?.action === "set" ? "success" : "error"}
      />

      <ConfirmModal
        show={!!promoToProcess}
        onCancel={() => setPromoToProcess(null)}
        onConfirm={confirmPromoAction}
        messageCopy={
          promoToProcess?.action === "set"
            ? `Set "${promoToProcess.name}" (${promoToProcess.id}) as promo?`
            : `Remove promo status from "${promoToProcess?.name}" (${promoToProcess?.id})?`
        }
        actionCopy={promoToProcess?.action === "set" ? "Set Promo" : "Remove Promo"}
        type={promoToProcess?.action === "set" ? "success" : "error"}
      />

      <ConfirmModal
        show={!!assumeToProcess}
        onCancel={() => setAssumeToProcess(null)}
        onConfirm={confirmAssumeAction}
        messageCopy={
          assumeToProcess?.action === "unassume"
            ? `Unassume "${assumeToProcess.name}" (${assumeToProcess.id})? This removes the event from your admin account.`
            : assumeToProcess?.action === "assume"
            ? `Assume "${assumeToProcess.name}" (${assumeToProcess.id})? This grants your admin account access to this event.`
            : ""
        }
        actionCopy={assumeToProcess?.action === "unassume" ? "Unassume" : "Assume"}
        type={assumeToProcess?.action === "unassume" ? "error" : "success"}
      />
    </div>
  );
};

export default EventTable;
